import { Button, CircularProgress, Divider, LinkButton, MuiAlert, ResponsiveDrawer, Typography } from '@fox/atoms';
import { FlexColumnSpaced, FlexRowLeftAligned, FlexWrapRowSpaced, Padded } from '@fox/layout';
import moment from 'moment';
import React, { useState } from 'react';
import { useReceipt, useReceipts } from '../../../Membership/query-hooks/query-implementations';
import { useContentContext } from '../../../content-context';
import { Receipt } from '../contracts';
import { dateFormat, dateTimeFormat, DrawerLine, formatCurrency, getReceiptTotal, ReceiptStatusChip, TABLE_BUTTON_STYLE } from '../shared';

export function ReceiptDetailsDesktop({
	record,
	useLinkedOrganisationData,
	allowedProviders
}: {
	record: Receipt;
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Button variant="secondary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				View details
			</Button>
			<ResponsiveDrawer
				placement="right"
				title="Receipt details"
				width={500}
				open={open}
				destroyOnClose
				onClose={() => {
					setOpen(false);
				}}
			>
				<ReceiptDetails
					receipt={record}
					isMobile={false}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedProviders={allowedProviders}
				/>
			</ResponsiveDrawer>
		</>
	);
}

export function ReceiptDetailsMobile({
	record,
	useLinkedOrganisationData,
	allowedProviders
}: {
	record: Receipt;
	useLinkedOrganisationData: boolean;
	allowedProviders?: string[];
}) {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Button variant="secondary" sx={TABLE_BUTTON_STYLE} onClick={() => setOpen(true)}>
				View details
			</Button>

			<ResponsiveDrawer
				placement="bottom"
				title="Receipt details"
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				destroyOnClose
				open={open}
				onClose={() => {
					setOpen(false);
				}}
			>
				<ReceiptDetails
					receipt={record}
					isMobile={true}
					useLinkedOrganisationData={useLinkedOrganisationData}
					allowedProviders={allowedProviders}
				/>
			</ResponsiveDrawer>
		</>
	);
}

function ReceiptDetails({
	receipt,
	isMobile,
	useLinkedOrganisationData,
	allowedProviders
}: {
	receipt: Receipt;
	isMobile: boolean;
	useLinkedOrganisationData?: boolean;
	allowedProviders?: string[];
}) {
	const { contentId, requestToken } = useContentContext();
	const {
		data: receipts,
		loading,
		error
	} = useReceipts({ contentId, requestToken, replacedBy: receipt.id, allowedProviders, useLinkedOrganisationData });
	if (loading) return <CircularProgress />;
	if (error) return <MuiAlert severity="error">{error}</MuiAlert>;
	return (
		<Padded>
			<FlexColumnSpaced>
				<DrawerLine title={'Receipt Number: '} children={receipt.receiptNo} />
				<DrawerLine title={'Transaction Reference: '} children={receipt.transactionRef} />
				<DrawerLine title={'Created Date: '} children={moment.utc(receipt.createdOn).local().format(dateTimeFormat)} />
				<DrawerLine title={'Effective Date: '} children={moment(receipt.effectiveDate).format(dateFormat)} />
				<DrawerLine title={'Status: '} children={<ReceiptStatusChip receipt={receipt} />} />
				{receipts?.totalResults > 0 ? (
					<>
						<Divider />
						<DrawerLine title={'This receipt resolved the following receipts: '} children={<></>} />
						<FlexWrapRowSpaced>
							{receipts.items.map(r => {
								return <ReplacementReceiptDetails key={r.id} id={r.id} isMobile={isMobile} />;
							})}
						</FlexWrapRowSpaced>
					</>
				) : null}
				{receipt.replacementId ? (
					<>
						<Divider />
						<DrawerLine title={'This receipt has been resolved by the following receipt: '} children={<></>} />
						<FlexRowLeftAligned>
							<ReplacementReceiptDetails id={receipt.replacementId} isMobile={isMobile} />
						</FlexRowLeftAligned>
					</>
				) : null}
				{receipt.description ? (
					<>
						<Divider />
						<DrawerLine title={'Description: '} children="" />
						{receipt.description}
					</>
				) : null}
				<Divider />
				{receipt.lineItems.map((item, idx) => {
					return (
						<React.Fragment key={idx}>
							<DrawerLine title={item.lineType.name} children={formatCurrency(item.amount, receipt.currency)} />
							<Typography style={{ alignSelf: 'start' }}>{item.comment}</Typography>
						</React.Fragment>
					);
				})}
				<Divider />
				<DrawerLine title={'Total: '} children={formatCurrency(getReceiptTotal(receipt), receipt.currency)} />
			</FlexColumnSpaced>
		</Padded>
	);
}

function ReplacementReceiptDetails({ id, isMobile }: { id: string; isMobile: boolean }) {
	const { contentId, requestToken } = useContentContext();
	const { data: receipt, error, loading } = useReceipt({ contentId, requestToken, id });
	const [open, setOpen] = useState<boolean>(false);

	if (loading) return <CircularProgress />;
	if (error) return <MuiAlert severity="error">{error}</MuiAlert>;

	return (
		<>
			<LinkButton onClick={() => setOpen(true)}>{receipt?.receiptNo}</LinkButton>
			{open ? (
				isMobile ? (
					<ResponsiveDrawer
						placement="bottom"
						title="Receipt details"
						destroyOnClose
						open={open}
						onClose={() => {
							setOpen(false);
						}}
					>
						<ReceiptDetails receipt={receipt} isMobile={true} />
					</ResponsiveDrawer>
				) : (
					<ResponsiveDrawer
						placement="right"
						title="Receipt details"
						width={500}
						open={open}
						destroyOnClose
						onClose={() => {
							setOpen(false);
						}}
					>
						<ReceiptDetails receipt={receipt} isMobile={false} />
					</ResponsiveDrawer>
				)
			) : null}
		</>
	);
}
